/**
 * Payment by bank-transfer QR (owner decision 2026-10-02: the only way customers pay) in the
 * Control Center: what the staff dialogs of "Giao dịch ngân hàng" and "Thanh toán" send, and who
 * sees them. Plain functions so the tests can check them without rendering
 * (tests/qr-payments.test.mjs; no imports, so Node loads it as it is). The server
 * (subsystems/commerce/facade/adapters/admin-api/src/admin/bank_payments.rs) decides.
 */

/** The ledger's NUMERIC(20,6): at most 14 integer digits. */
const MAX_DIGITS = 14;

/** Characters of a transfer code after its prefix (the payment core's `CODE_ALPHABET`). */
export const CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';

/**
 * Whole đồng as staff type them ("1.000.000", "1 000 000", "1000000") as a plain number string,
 * or null (zero, a fraction, too large, not a number).
 */
export function wholeDong(text: string): string | null {
  const t = text.trim();
  // Thousands grouped by threes with `.`, `,` or a space, or no grouping (as "Điều chỉnh số dư").
  if (!/^(\d+|\d{1,3}([., ]\d{3})+)$/.test(t)) return null;
  const a = t.replace(/[., ]/g, '').replace(/^0+(?=\d)/, '');
  return /[1-9]/.test(a) && a.length <= MAX_DIGITS ? a : null;
}

/**
 * A transfer content code as the payment core makes it: a prefix of 2–6 capital letters or digits
 * (a letter first) and 8 characters of `CODE_ALPHABET`, e.g. `TINO7K3QX9AB`. Case and spaces as
 * typed are forgiven; the server looks the code up in capitals.
 */
export function transferCode(text: string): string | null {
  const c = text.replace(/\s+/g, '').toUpperCase();
  return new RegExp(`^[A-Z][A-Z0-9]{1,5}[${CODE_ALPHABET}]{8}$`).test(c) ? c : null;
}

type Grants = readonly { scope: string; permissions: readonly string[] }[] | null;

/**
 * Whether to offer the staff actions on bank transactions and the manual confirmation of a
 * transfer: a platform-wide (`Global`) grant holding `billing.adjust`, as the server requires.
 * Never to customer users.
 */
export function mayHandlePayments(grants: Grants, client: boolean): boolean {
  return !client && Boolean(grants?.some((g) => g.scope === 'Global' && g.permissions.includes('billing.adjust')));
}

/**
 * Whether to offer "make a QR code" for a customer (a top-up or an order): `billing.adjust` on any
 * scope (the server checks it covers the customer).
 */
export function mayMakeCodes(grants: Grants, client: boolean): boolean {
  return !client && Boolean(grants?.some((g) => g.permissions.includes('billing.adjust')));
}

/**
 * Why the money a gateway reported for an attempt needs a look (it reached the wallet anyway,
 * ADR-028 decision 4): it arrived while the attempt is not CAPTURED, or its amount is not the
 * attempt's. Null when it fits or no money arrived. Both amounts are whole đồng.
 */
export function moneyMisfit(s: { status: string; amount: string; money_amount: string | null }): 'NOT_CAPTURED' | 'AMOUNT' | null {
  if (s.money_amount === null) return null;
  if (s.status !== 'CAPTURED') return 'NOT_CAPTURED';
  return s.money_amount.split('.')[0] === s.amount.split('.')[0] ? null : 'AMOUNT';
}

/** Whether a manual confirmation of `amount` needs a second person's approval first. */
export function needsSecondApproval(amount: string, threshold: string): boolean {
  try {
    return BigInt(amount) >= BigInt(threshold.split('.')[0] ?? threshold);
  } catch {
    return false;
  }
}
